import hiringManager from "@/assets/boss.png";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { JobAnalysisApiResponse } from '@/lib/api';
import { AlertCircle, Brain, CheckCircle, Lightbulb, Lock, MessageCircle, MessageCircleDashed, Target, TrendingDown, TrendingUp } from 'lucide-react';
import { useState } from 'react';

export type AnalysisState = 'none' | 'not_generated' | 'generating' | 'ready';

interface JobAnalysisProps {
  analysis: JobAnalysisApiResponse | null;
  state: AnalysisState;
  isSubscribed: boolean;
  onGetPremium: () => void;
  onGenerate: () => void;
  generating?: boolean;
  demoMode?: boolean;
  embed?: boolean;
  demoAnalysis?: JobAnalysisApiResponse;
}

const getFitRatingConfig = (rating: string) => {
  switch (rating.toUpperCase()) {
    case 'PERFECT':
      return { 
        color: 'bg-green-100 text-green-800 border-green-200', 
        icon: CheckCircle, 
        iconColor: 'text-green-600',
        bgGradient: 'from-green-50 to-emerald-50'
      };
    case 'GREAT':
      return { 
        color: 'bg-blue-100 text-blue-800 border-blue-200', 
        icon: TrendingUp, 
        iconColor: 'text-blue-600',
        bgGradient: 'from-blue-50 to-cyan-50'
      };
    case 'GOOD':
      return { 
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200', 
        icon: Target, 
        iconColor: 'text-yellow-600',
        bgGradient: 'from-yellow-50 to-amber-50'
      };
    case 'POOR':
      return { 
        color: 'bg-red-100 text-red-800 border-red-200', 
        icon: TrendingDown, 
        iconColor: 'text-red-600',
        bgGradient: 'from-red-50 to-rose-50'
      };
    default:
      return { 
        color: 'bg-gray-100 text-gray-800 border-gray-200', 
        icon: AlertCircle, 
        iconColor: 'text-gray-600',
        bgGradient: 'from-gray-50 to-slate-50'
      };
  }
};

function FullAnalysis({ analysis }: { analysis: JobAnalysisApiResponse }) {
  const [activeTab, setActiveTab] = useState<string>('description');
  const fitConfig = getFitRatingConfig(analysis.fit_rating);
  const FitIcon = fitConfig.icon as any;

  const tabs = [
    { id: 'description', label: 'Description', icon: MessageCircle },
    { id: 'competitive', label: 'Competitive Analysis', icon: TrendingUp },
    { id: 'hiring', label: 'Hiring Manager Thoughts', icon: Brain },
    { id: 'recommendations', label: 'Recommendations', icon: Lightbulb }
  ];

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      <div className={`bg-gradient-to-br ${fitConfig.bgGradient} p-4 sm:p-6 border-b border-gray-200`}>
        <div className="flex items-center gap-3">
          <FitIcon className={`h-6 w-6 sm:h-8 sm:w-8 ${fitConfig.iconColor}`} />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Job Fit Analysis</h2>
          <Badge className={`ml-auto ${fitConfig.color} font-semibold text-sm sm:text-base px-3 py-1`}>
            {analysis.fit_rating}
          </Badge>
        </div>
      </div>

      <div className="border-b border-gray-200 bg-gray-50">
        <div className="flex overflow-x-auto">
          {tabs.map((tab) => {
            const TabIcon = tab.icon as any;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <TabIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">
                  {tab.id === 'description' ? 'Info' : 
                   tab.id === 'competitive' ? 'Analysis' :
                   tab.id === 'hiring' ? 'Manager' : 'Tips'}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {activeTab === 'description' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 sm:p-6 border-l-4 border-blue-500">
              <h4 className="font-semibold text-blue-900 mb-3 flex items-center text-base sm:text-lg">
                <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                Analysis Overview
              </h4>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                {analysis.description || 'No description available.'}
              </p>
            </div>
          </div>
        )}

        {activeTab === 'competitive' && (
          <div className="space-y-6">
            {analysis.competitive_advantage && analysis.competitive_advantage.length > 0 && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 sm:p-6 border-l-4 border-green-500">
                <h4 className="font-semibold text-green-900 mb-3 sm:mb-4 flex items-center text-base sm:text-lg">
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                  Your Competitive Advantages
                </h4>
                <ul className="space-y-3">
                  {analysis.competitive_advantage.map((advantage, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm sm:text-base text-gray-700">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                      <span className="break-words leading-relaxed">{advantage}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {analysis.areas_for_improvement && analysis.areas_for_improvement.length > 0 && (
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 sm:p-6 border-l-4 border-orange-500">
                <h4 className="font-semibold text-orange-900 mb-3 sm:mb-4 flex items-center text-base sm:text-lg">
                  <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                  Areas to Address
                </h4>
                <ul className="space-y-3">
                  {analysis.areas_for_improvement.map((area, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm sm:text-base text-gray-700">
                      <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                      <span className="break-words leading-relaxed">{area}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {activeTab === 'hiring' && (
          <div className="space-y-4">
            {analysis.hiring_manager_thoughts && analysis.hiring_manager_thoughts.length > 0 ? (
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-4 sm:p-6 border-l-4 border-purple-500">
                <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
                  <div className="flex-shrink-0 w-20 sm:w-24 mx-auto sm:mx-0">
                    <img
                      src={hiringManager}
                      alt="Hiring Manager"
                      className="w-20 sm:w-36 object-contain"
                    />
                  </div>
                  <div className="flex-1 w-full">
                    <h4 className="font-semibold text-purple-900 mb-3 flex items-center text-base sm:text-lg">
                      <MessageCircleDashed className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                      Hiring Manager's Perspective
                    </h4>
                    <div className="space-y-3">
                      {analysis.hiring_manager_thoughts.map((thought, index) => (
                        <div key={index} className="bg-white rounded-lg p-4 shadow-sm border border-purple-200">
                          <p className="text-gray-700 leading-relaxed italic text-sm sm:text-base">
                            "{thought}"
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <MessageCircleDashed className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Hiring Manager Insights</h3>
                <p className="text-gray-600">Hiring manager thoughts are not available for this analysis.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'recommendations' && (
          <div className="space-y-4">
            {analysis.recommendations && analysis.recommendations.length > 0 ? (
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 sm:p-6 border-l-4 border-blue-500">
                <h4 className="font-semibold text-blue-900 mb-3 sm:mb-4 flex items-center text-base sm:text-lg">
                  <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                  Our Recommendations
                </h4>
                <ul className="space-y-3">
                  {analysis.recommendations.map((recommendation, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm sm:text-base text-gray-700">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                      <span className="break-words leading-relaxed">{recommendation}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <Lightbulb className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Recommendations Available</h3>
                <p className="text-gray-600">Recommendations are not available for this analysis.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function JobAnalysis({ analysis, state, isSubscribed, onGetPremium, onGenerate, generating, demoMode, embed, demoAnalysis }: JobAnalysisProps) {
  // Demo mode: render provided demo analysis or a sensible default
  if (demoMode) {
    const defaultDemo: JobAnalysisApiResponse = {
      id: 'demo-analysis-1',
      job_id: 'demo-job-1',
      user_id: 'demo-user',
      description:
        'Overall strong alignment with the role. Your front-end leadership, performance wins, and cross-functional collaboration map well to the expectations for a Senior Frontend Engineer.',
      fit_rating: 'GREAT',
      competitive_advantage: [
        'Led design system adoption that improved UI consistency across 12 products.',
        'Demonstrated measurable impact with performance improvements of 30%+',
        'Strong collaboration with design and product; clear user-centric decision making.',
      ],
      areas_for_improvement: [
        'Expand on end-to-end ownership for complex initiatives (planning through post-launch).',
        'Highlight mentoring or team leadership examples to reinforce seniority.',
      ],
      hiring_manager_thoughts: [
        'Candidate shows strong product intuition and thoughtful trade-off decisions.',
        'Would like to hear more about how they handle ambiguity and prioritization across stakeholders.',
      ],
      recommendations: [
        'Add a concise summary statement at the top of your resume focused on front-end leadership and impact.',
        'Include one story demonstrating complex cross-team coordination and how you drove alignment.',
        'Quantify the business outcomes tied to your performance work (e.g., conversion, engagement).',
      ],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    return <FullAnalysis analysis={analysis ?? demoAnalysis ?? defaultDemo} />;
  }

  if (analysis) {
    return <FullAnalysis analysis={analysis} />;
  }

  const fitConfig = getFitRatingConfig('GOOD');
  const FitIcon = fitConfig.icon as any;
  const [activeTab, setActiveTab] = useState<string>('description');

  const tabs = [
    { id: 'description', label: 'Description', icon: MessageCircle },
    { id: 'competitive', label: 'Competitive Analysis', icon: TrendingUp },
    { id: 'hiring', label: 'Hiring Manager Thoughts', icon: Brain },
    { id: 'recommendations', label: 'Recommendations', icon: Lightbulb }
  ];

  const RightHeaderArea = () => {
    if (!isSubscribed) {
      return <Button size="sm" onClick={onGetPremium}>Get Premium</Button>;
    }
    if (state === 'not_generated') {
      return (
        <Button size="sm" onClick={onGenerate} disabled={generating}>
          {generating ? 'Starting…' : 'Generate Resume Analysis'}
        </Button>
      );
    }
    if (state === 'generating') {
      return (
        <div className="ml-auto bg-blue-100 text-blue-800 border-blue-200 font-semibold text-xs sm:text-sm px-3 py-1 rounded-full animate-pulse">
          Generating…
        </div>
      );
    }
    return null;
  };

  return (
    <div className="relative bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      {!demoMode && !isSubscribed && (
        <div className="absolute top-3 left-3 z-10">
          <div className="rounded-full bg-white shadow-md p-1.5 ring-1 ring-slate-200">
            <Lock className="h-6 w-6 text-slate-700" />
          </div>
        </div>
      )}

      <div className={`bg-gradient-to-br ${fitConfig.bgGradient} p-4 sm:p-6 border-b border-gray-200`}>
        <div className="flex items-center gap-3 sm:gap-4">
          <FitIcon className={`h-6 w-6 sm:h-8 sm:w-8 ${fitConfig.iconColor}`} />
          <div className="min-w-0 flex-1">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Job Fit Analysis</h2>
            <p className="text-gray-700 text-sm sm:text-base mt-1">
              {isSubscribed
                ? (state === 'not_generated' ? 'Generate your personalized analysis for this role.' : 'We are preparing your analysis…')
                : "Here's a preview of what you get with HiredCoach Premium"}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <RightHeaderArea />
          </div>
        </div>
      </div>

      <div className="border-b border-gray-200 bg-gray-50">
        <div className="flex overflow-x-auto">
          {tabs.map((tab) => {
            const TabIcon = tab.icon as any;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <TabIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">
                  {tab.id === 'description' ? 'Info' : 
                   tab.id === 'competitive' ? 'Analysis' :
                   tab.id === 'hiring' ? 'Manager' : 'Tips'}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {activeTab === 'description' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 sm:p-6 border-l-4 border-blue-500">
              <h4 className="font-semibold text-blue-900 mb-3 flex items-center text-base sm:text-lg">
                <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                Analysis Overview
              </h4>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                Unlock a personalized analysis of how your resume matches this role. See a clear summary of your fit and what matters most to hiring teams.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'competitive' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 sm:p-6 border-l-4 border-green-500">
              <h4 className="font-semibold text-green-900 mb-3 sm:mb-4 flex items-center text-base sm:text-lg">
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                Your Competitive Advantages
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm sm:text-base text-gray-700">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="break-words leading-relaxed">Specific strengths from your background that align with the role requirements</span>
                </li>
                <li className="flex items-start gap-3 text-sm sm:text-base text-gray-700">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="break-words leading-relaxed">Unique experiences that set you apart from other candidates</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 sm:p-6 border-l-4 border-orange-500">
              <h4 className="font-semibold text-orange-900 mb-3 sm:mb-4 flex items-center text-base sm:text-lg">
                <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                Areas to Address
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm sm:text-base text-gray-700">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="break-words leading-relaxed">Potential gaps that recruiters may question—and how to position them</span>
                </li>
                <li className="flex items-start gap-3 text-sm sm:text-base text-gray-700">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="break-words leading-relaxed">Suggestions to strengthen your application</span>
                </li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'hiring' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg p-4 sm:p-6 border-l-4 border-purple-500">
              <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
                <div className="flex-shrink-0 w-20 sm:w-24 mx-auto sm:mx-0">
                  <img
                    src={hiringManager}
                    alt="Hiring Manager"
                    className="w-20 sm:w-36 object-contain"
                  />
                </div>
                <div className="flex-1 w-full">
                  <h4 className="font-semibold text-purple-900 mb-3 flex items-center text-base sm:text-lg">
                    <MessageCircleDashed className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                    Hiring Manager's Perspective
                  </h4>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-200">
                      <p className="text-gray-700 leading-relaxed italic text-sm sm:text-base">
                        "Get insight into how a hiring manager is likely to perceive your resume for this role."
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-200">
                      <p className="text-gray-700 leading-relaxed italic text-sm sm:text-base">
                        "Understand what stands out, what raises questions, and how to frame your experience."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'recommendations' && (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 sm:p-6 border-l-4 border-blue-500">
              <h4 className="font-semibold text-blue-900 mb-3 sm:mb-4 flex items-center text-base sm:text-lg">
                <Lightbulb className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                Our Recommendations
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-sm sm:text-base text-gray-700">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="break-words leading-relaxed">Actionable suggestions to improve your resume for this job</span>
                </li>
                <li className="flex items-start gap-3 text-sm sm:text-base text-gray-700">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="break-words leading-relaxed">Tips to boost response rates and land more interviews</span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
